import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Image as ImageIcon, Search, Trash2 } from 'lucide-react';
import { formatBytes } from '@/lib/cn';
import { useI18n } from '@/i18n';
import { api, toApiError } from '@/lib/api';
import { useInvalidate, useMedia, useMediaUsage } from '@/lib/queries';
import { useToast } from '@/components/ui/Toast';
import type { Locale, MediaItem } from '@/lib/types';
import { LOCALES } from '@/lib/types';
import { PageHeader } from '@/components/PageHeader';
import { MediaCard, Segmented } from '@/components/MediaPicker';
import { Button } from '@/components/ui/Button';
import { Field, Input } from '@/components/ui/Field';
import { Drawer, Modal } from '@/components/ui/primitives';
import { EmptyState, Notice, Skeleton } from '@/components/ui/Feedback';

/**
 * The library. Uploading happens inside the picker (reused here), so there is
 * one upload path in the whole application and one place its errors are
 * handled.
 *
 * "Unused" is a filter, never an auto-delete (§6). A media item referenced by
 * nothing is a candidate for review, not a candidate for deletion by a script.
 */
export function Media() {
  const { t, formatDate } = useI18n();
  const toast = useToast();
  const invalidate = useInvalidate();
  const [params, setParams] = useSearchParams();

  const [query, setQuery] = useState('');
  const filter = (params.get('filter') === 'unused' ? 'unused' : 'all') as 'all' | 'unused';
  const [selected, setSelected] = useState<MediaItem | null>(null);
  const [removing, setRemoving] = useState<MediaItem | null>(null);

  const { data, isLoading } = useMedia({ q: query || undefined, filter });
  const usage = useMediaUsage(selected?.id ?? null);

  return (
    <>
      <PageHeader title={t.media.title} subtitle={t.media.subtitle}>
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative min-w-[14rem] max-w-sm flex-1">
            <Search aria-hidden className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-faint" />
            <Input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={t.common.search}
              aria-label={t.common.search}
              className="pl-9"
            />
          </div>
          <Segmented
            value={filter}
            onChange={(value) => setParams(value === 'unused' ? { filter: 'unused' } : {})}
            options={[
              { value: 'all', label: t.media.filterAll },
              { value: 'unused', label: t.media.filterUnused },
            ]}
          />
        </div>
      </PageHeader>

      <div className="mx-auto w-full max-w-content px-6 pb-12">
        {isLoading ? (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
            {Array.from({ length: 10 }, (_, i) => (
              <Skeleton key={i} className="aspect-[4/3] rounded-card" />
            ))}
          </div>
        ) : (data?.items.length ?? 0) === 0 ? (
          <div className="card">
            <EmptyState icon={<ImageIcon className="h-6 w-6" />} title={t.media.emptyTitle} hint={t.media.emptyHint} />
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
            {(data?.items ?? []).map((item) => (
              <MediaCard key={item.id} media={item} onPick={() => setSelected(item)} />
            ))}
          </div>
        )}
      </div>

      <Drawer
        open={Boolean(selected)}
        onOpenChange={(open) => !open && setSelected(null)}
        title={selected?.original ?? ''}
        description={
          selected
            ? `${selected.width}×${selected.height} · ${formatBytes(selected.size)} · ${formatDate(selected.createdAt)}`
            : undefined
        }
        footer={
          <Button
            variant="ghost"
            icon={<Trash2 className="h-4 w-4" />}
            onClick={() => selected && setRemoving(selected)}
            className="text-accent-600 dark:text-accent-400"
          >
            {t.common.delete}
          </Button>
        }
      >
        {selected && (
          <div className="flex flex-col gap-5">
            <img src={selected.url} alt="" className="w-full rounded-card border border-line" />

            {/* Alt text per locale, surfaced where the image is (§6). */}
            {LOCALES.map((locale) => (
              <AltField key={locale} media={selected} locale={locale} onSaved={() => void invalidate(['media'])} />
            ))}

            <div>
              <p className="eyebrow mb-2">{t.media.usedBy}</p>
              {usage.isLoading ? (
                <Skeleton className="h-8 w-full" />
              ) : (usage.data?.usedBy.length ?? 0) === 0 ? (
                <p className="text-ui text-muted">{t.media.unused}</p>
              ) : (
                <ul className="flex flex-col gap-1">
                  {usage.data?.usedBy.map((ref) => (
                    <li key={`${ref.entity}-${ref.id}`} className="font-mono text-code text-muted">
                      {ref.entity} · {ref.label}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            {/* URL + nusxalash olib tashlandi: marketing xodimiga xom havola
                kerak emas — rasmni tanlash muqova/matn ichida ishlaydi. */}
          </div>
        )}
      </Drawer>

      <Modal
        open={Boolean(removing)}
        onOpenChange={(open) => !open && setRemoving(null)}
        title={t.common.delete}
        description={removing?.original}
        footer={
          <>
            <Button variant="outline" onClick={() => setRemoving(null)}>
              {t.common.cancel}
            </Button>
            <Button
              variant="danger"
              onClick={async () => {
                if (!removing) return;
                try {
                  await api.delete(`/media/${removing.id}`);
                  void invalidate(['media']);
                  setRemoving(null);
                  setSelected(null);
                  toast({ tone: 'info', message: t.common.deleted });
                } catch (error) {
                  const failure = toApiError(error);
                  // 409 MEDIA_IN_USE arrives with the list of referencing
                  // entities rather than orphaning a cover image.
                  toast({
                    tone: 'error',
                    message: failure.message,
                    detail: failure.usedBy?.map((ref) => ref.label).join(', '),
                  });
                }
              }}
            >
              {t.common.delete}
            </Button>
          </>
        }
      >
        {(usage.data?.usedBy.length ?? 0) > 0 && <Notice tone="warn">{t.media.deleteInUse}</Notice>}
      </Modal>
    </>
  );
}

function AltField({ media, locale, onSaved }: { media: MediaItem; locale: Locale; onSaved: () => void }) {
  const { t } = useI18n();
  const [value, setValue] = useState(media.alt[locale] ?? '');

  return (
    <Field
      label={`${t.editor.altLabel} — ${locale.toUpperCase()}`}
      hint={locale === 'uz' ? t.editor.altHint : undefined}
    >
      {({ id, describedBy }) => (
        <Input
          id={id}
          aria-describedby={describedBy}
          value={value}
          onChange={(event) => setValue(event.target.value)}
          onBlur={async () => {
            if (value === (media.alt[locale] ?? '')) return;
            const key = `alt${locale.charAt(0).toUpperCase()}${locale.slice(1)}`;
            await api.post(`/media/${media.id}/alt`, { [key]: value || null }).catch(() => undefined);
            onSaved();
          }}
        />
      )}
    </Field>
  );
}
